/**
 * <StudentFinishedActivityPage> — l'élève relit une activité qu'il a finie (page).
 *
 * L'ancien `ActivityFinishedScreen`, ouvert depuis les activités faites, les réalisations ou la
 * messagerie. Deux vues, l'une à la place de l'autre (`workOpen`, contrôlé) :
 *  - **l'activité** : en tête, le retour et le titre ; puis la matière ou le chapitre, le statut
 *    (`<ActivityStatusBadge>`) et le chrono « 12 / 20 min » (`<ActivityDetailsHeader>`), et l'énoncé
 *    qui défile (`<ActivityStatement>`, en grand comme pendant l'exécution). En pied, la barre du
 *    rendu, à la place de la barre de copie de l'exécution et dans sa forme : les pages envoyées en
 *    vignettes (« +N » au-delà de ce qui tient), « Refaire » quand l'écran l'offre (à la place de
 *    l'appareil photo, Wajih 25/10 : refaire est à portée, pour oser envoyer en cours de route), puis
 *    le bouton du rendu, « Voir ma correction », « Voir mon rendu », ou « Correction en cours… »
 *    pendant que Sam corrige ;
 *  - **le rendu** (« Mon rendu », l'ancienne boîte `ActivityDetailsModal`), dans la forme de la copie
 *    corrigée qui suit l'envoi (`<StudentCorrectionPage step="copy">`) : la croix en tête ; Sam qui
 *    corrige encore (`<CorrectionMoment working>`) ; l'appréciation, dite par Sam, tant qu'il n'y a
 *    pas d'image de correction ; la copie, ses pastilles et ses points, ou l'enregistrement qui se
 *    joue (`<ActivityWork>`) ; « La correction t'a aidé ? » quand la correction est là ; « Aucun
 *    travail n'a été soumis… » quand rien ne l'a été. En pied, « Refaire l'activité » quand l'écran
 *    l'offre.
 *
 * Rien ne s'y exécute : ni chrono, ni appareil photo, ni « J'ai fini ». Contrôlée : la copie, la
 * correction, l'avis, la lecture de l'enregistrement et l'échec à dire arrivent en props ; chaque
 * geste remonte.
 */
import React from "react"
import { type LayoutChangeEvent, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UIActivityDetails, type UIStatementReading } from "../../../ports/primary/ui/types"
import { ChampionAvatar } from "../../atoms/Avatar/ChampionAvatar"
import { Button } from "../../atoms/Buttons/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { MediaThumbnail } from "../../atoms/MediaThumbnail"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { DelayedSkeleton, Skeleton } from "../../atoms/Skeleton"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../../molecules/Card"
import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { ConversationHeader } from "../../molecules/ConversationHeader"
import { CorrectionFeedback, type UICorrectionFeedback } from "../../molecules/CorrectionFeedback"
import { CorrectionMoment } from "../../molecules/CorrectionMoment"
import { COPY_BAR_THUMB_SIZE, useCopyBarThumbSlots } from "../../organisms/ActivityCopyBar/ActivityCopyBar"
import { ActivityDetailsHeader } from "../../organisms/ActivityDetailsHeader"
import { ActivityStatement } from "../../organisms/ActivityStatement"
import { ActivityWork, type ActivityWorkAudio } from "../../organisms/ActivityWork"
import { PageTemplate } from "../../templates/PageTemplate"

/** Où en est la correction de la copie : elle se fait, elle est là, ou il n'y en a pas. */
export type StudentFinishedCorrection = "none" | "generating" | "ready"

export type StudentFinishedActivityPageProps = {
    /** L'activité : son en-tête, son énoncé, sa copie (`work`, `null` quand rien n'a été rendu). */
    activity: UIActivityDetails
    /** Une figure est annoncée, le serveur la dessine encore. Défaut `false`. */
    figurePending?: boolean
    /** La lecture de l'élève (« Ma lecture », `<ActivityStatement reading>`). Absente : la feuille standard. */
    statementReading?: UIStatementReading
    /** Le retour. Absent : pas de flèche. */
    onBack?: () => void
    /**
     * Où se lit le titre. `bar` (défaut) : l'en-tête de l'écran. `inline` : en tête du contenu — dans
     * le volet du live, sous le seul en-tête du live, comme `<StudentActivityPage heading="inline">`.
     */
    heading?: "bar" | "inline"
    /** Ce que l'activité rend : des photos (défaut), ou un enregistrement. Le glyphe du bouton. */
    medium?: "photo" | "audio"
    /** Défaut `none`. */
    correction?: StudentFinishedCorrection
    /** La copie se relit et n'a encore rien montré : le bouton tourne, le rendu montre sa forme. */
    workLoading?: boolean
    /** Le bouton du rendu, ou une vignette touchée. */
    onOpenWork: () => void

    // ── Le rendu ──
    /** Le rendu est ouvert à la place de l'activité. Défaut `false`. */
    workOpen?: boolean
    /** La croix du rendu. */
    onCloseWork?: () => void
    /** L'appréciation, dite par Sam tant que la copie n'a pas d'image de correction. */
    comment?: string
    /** « La correction t'a aidé ? », quand la correction est là. */
    feedback?: UICorrectionFeedback | null
    onFeedback?: (value: UICorrectionFeedback) => void
    feedbackSubmitting?: boolean
    /** L'avis n'a pas pu partir : ce qu'on en dit, sous les pouces. Absent : rien. */
    feedbackError?: string | null
    /** La lecture de l'enregistrement d'une activité orale. */
    audio?: ActivityWorkAudio
    /** « Refaire l'activité ». Absent : pas de bouton. */
    onRedo?: () => void
    redoing?: boolean

    /** L'échec à dire, dans une boîte à un bouton. `null` ou absent : aucune. */
    notice?: { title: string; message?: string } | null
    onDismissNotice?: () => void
    testID?: string
}

/** L'énoncé se lit d'un trait sur un grand écran, comme pendant l'exécution. */
const READING_WIDTH = 760
/** La colonne de la copie, celle de la copie corrigée qui suit l'envoi. */
const COPY_WIDTH = 760
/** Les largeurs de barre où trois, puis deux vignettes tiennent à côté du bouton ; en deçà, une. */
const THREE_THUMBS_FROM = 380
const TWO_THUMBS_FROM = 320
/**
 * Avec « Refaire » (124 px mesurés), les vignettes ne prennent que la place qui laisse au bouton du
 * rendu son plus long libellé sur une ligne : « Correction en cours… », 190 px avec ses marges, 218 avec
 * l'icône qu'il prend dès deux vignettes. Une vignette à partir de 390 px de barre, deux à 470, trois
 * à 530. En deçà de 390 (la plupart des téléphones, la colonne du live), aucune : le bouton du rendu,
 * qui ouvre la même chose, prend sa place.
 */
const REDO_ONE_THUMB_FROM = 390
const REDO_TWO_THUMBS_FROM = 470
const REDO_THREE_THUMBS_FROM = 530
const COMMENT_AVATAR = 40
const LOADING_HEIGHT = 240
const NOOP = () => {}

export const FINISHED_ACTIVITY_TEXTS = {
    seeCorrection: "Voir ma correction",
    seeWork: "Voir mon rendu",
    correcting: "Correction en cours…",
    workTitle: "Mon rendu",
    close: "Fermer",
    samCorrecting: "Je suis en train de corriger ton travail, tiens-toi prêt !",
    generating: "Génération de la correction…",
    loading: "Chargement de ton rendu",
    noWork: "Aucun travail n'a été soumis pour cette activité.",
    redo: "Refaire l'activité",
    redoShort: "Refaire",
    page: (n: number) => `Page ${n} de ta copie`,
    ok: "OK",
}

export function StudentFinishedActivityPage(props: StudentFinishedActivityPageProps) {
    const { workOpen = false, notice, onDismissNotice, testID } = props
    return (
        <>
            {workOpen ? <WorkView {...props} /> : <ActivityView {...props} />}
            <ConfirmDialog
                visible={notice != null}
                title={notice?.title ?? ""}
                message={notice?.message}
                confirmLabel={FINISHED_ACTIVITY_TEXTS.ok}
                hideCancel
                danger={false}
                onConfirm={onDismissNotice ?? NOOP}
                onCancel={onDismissNotice ?? NOOP}
                testID={testID != null ? `${testID}-notice` : undefined}
            />
        </>
    )
}

function buttonLabel(correction: StudentFinishedCorrection): string {
    if (correction === "generating") return FINISHED_ACTIVITY_TEXTS.correcting
    if (correction === "ready") return FINISHED_ACTIVITY_TEXTS.seeCorrection
    return FINISHED_ACTIVITY_TEXTS.seeWork
}

/** Les pages de la copie : les images du rendu, sans l'image de correction qui les précède. */
function copyPages(activity: UIActivityDetails): string[] {
    const images = activity.work?.images ?? []
    return activity.work?.hasCorrection === true ? images.slice(1) : images
}

function ActivityView(props: StudentFinishedActivityPageProps) {
    const {
        activity,
        figurePending = false,
        statementReading,
        onBack,
        heading = "bar",
        medium = "photo",
        correction = "none",
        workLoading = false,
        onOpenWork,
        onRedo,
        redoing = false,
        testID,
    } = props
    const withRedo = onRedo != null
    const slots = useCopyBarThumbSlots(
        withRedo ? REDO_THREE_THUMBS_FROM : THREE_THUMBS_FROM,
        withRedo ? REDO_TWO_THUMBS_FROM : TWO_THUMBS_FROM
    )
    const oneThumb = useWidthAtLeast(REDO_ONE_THUMB_FROM)
    const maxThumbs = !withRedo || oneThumb.atLeast ? slots.maxThumbs : 0
    const onLayout = (event: LayoutChangeEvent) => {
        slots.onLayout(event)
        oneThumb.onLayout(event)
    }

    const pages = medium === "photo" ? copyPages(activity) : []
    const visible = pages.slice(0, maxThumbs)
    const more = pages.length - visible.length

    const footer = (
        <View onLayout={onLayout}>
            <Row gap="3" align="center">
                {visible.length > 0 && (
                    <Row gap="2" align="center">
                        {visible.map((uri, index) => (
                            <MediaThumbnail
                                key={`${index}-${uri}`}
                                uri={uri}
                                size={COPY_BAR_THUMB_SIZE}
                                moreCount={index === visible.length - 1 ? more : 0}
                                onPress={onOpenWork}
                                accessibilityLabel={FINISHED_ACTIVITY_TEXTS.page(index + 1)}
                            />
                        ))}
                    </Row>
                )}
                {onRedo != null && (
                    <Button
                        label={FINISHED_ACTIVITY_TEXTS.redoShort}
                        accessibilityLabel={FINISHED_ACTIVITY_TEXTS.redo}
                        iconName="redo"
                        variant="outline"
                        onPress={onRedo}
                        loading={redoing}
                    />
                )}
                <View style={styles.grow}>
                    <Button
                        label={buttonLabel(correction)}
                        iconName={slots.roomy ? (medium === "audio" ? "microphone" : "camera") : undefined}
                        onPress={onOpenWork}
                        loading={workLoading && activity.work == null}
                        fullWidth
                    />
                </View>
            </Row>
        </View>
    )

    return (
        <PageTemplate
            header={
                heading === "bar" ? (
                    <ConversationHeader name={activity.title} onBack={onBack} maxContentWidth={READING_WIDTH} />
                ) : undefined
            }
            maxContentWidth={READING_WIDTH}
            padding="4"
            footer={footer}
            testID={testID}
        >
            <Stack gap="5">
                <ActivityDetailsHeader activity={activity} showTitle={heading === "inline"} />
                <ActivityStatement
                    statement={activity.statement}
                    mediaLayout="full"
                    figurePending={figurePending}
                    reading={statementReading}
                />
            </Stack>
        </PageTemplate>
    )
}

function WorkView(props: StudentFinishedActivityPageProps) {
    const {
        activity,
        correction = "none",
        workLoading = false,
        onCloseWork,
        comment,
        feedback,
        onFeedback,
        feedbackSubmitting,
        feedbackError,
        audio,
        onRedo,
        redoing = false,
        testID,
    } = props

    const generating = correction === "generating"
    const work = activity.work ?? null
    // L'appréciation tant que la copie n'a pas d'image de correction : celle-ci la remplace.
    const showComment = !generating && work?.hasCorrection !== true && comment != null && comment !== ""

    // Le pied, à part : un `loading` posé dans le gabarit se lirait comme celui de la page.
    const redoButton =
        onRedo != null ? (
            <Button
                label={FINISHED_ACTIVITY_TEXTS.redo}
                iconName="redo"
                variant="outline"
                onPress={onRedo}
                loading={redoing}
                fullWidth
            />
        ) : undefined

    return (
        <PageTemplate
            header={
                <ConversationHeader
                    name={FINISHED_ACTIVITY_TEXTS.workTitle}
                    maxContentWidth={COPY_WIDTH}
                    end={
                        <IconButton
                            iconName="times"
                            appearance="transparent"
                            iconColor="primary"
                            size={44}
                            iconSize={20}
                            onPress={onCloseWork}
                            accessibilityLabel={FINISHED_ACTIVITY_TEXTS.close}
                        />
                    }
                />
            }
            maxContentWidth={COPY_WIDTH}
            padding="4"
            footer={redoButton}
            testID={testID != null ? `${testID}-work` : undefined}
        >
            <Stack gap="5">
                {generating ? (
                    <CorrectionMoment
                        title={FINISHED_ACTIVITY_TEXTS.samCorrecting}
                        detail={FINISHED_ACTIVITY_TEXTS.generating}
                        working
                    />
                ) : (
                    <>
                        {showComment && (
                            <Card p="4">
                                <Row gap="3" align="start">
                                    <ChampionAvatar name="sam" size={COMMENT_AVATAR} />
                                    <Text style={styles.grow}>{comment}</Text>
                                </Row>
                            </Card>
                        )}
                        {work != null ? (
                            <ActivityWork work={work} audio={audio} />
                        ) : workLoading ? (
                            <DelayedSkeleton accessibilityLabel={FINISHED_ACTIVITY_TEXTS.loading}>
                                <Skeleton shape="block" height={LOADING_HEIGHT} />
                            </DelayedSkeleton>
                        ) : (
                            <Text color="text-muted">{FINISHED_ACTIVITY_TEXTS.noWork}</Text>
                        )}
                        {work?.hasCorrection === true && onFeedback != null && (
                            <CorrectionFeedback
                                value={feedback ?? null}
                                onChange={onFeedback}
                                submitting={feedbackSubmitting}
                                error={feedbackError}
                            />
                        )}
                    </>
                )}
            </Stack>
        </PageTemplate>
    )
}

const styles = StyleSheet.create(() => ({
    grow: { flex: 1, minWidth: 0 },
}))
